/**
 * Item label settings — NORMALISATION RULES.
 *
 * Several settings constrain each other. Left alone they drift into combinations the print
 * pipeline cannot honour: a barcode delimiter with no barcode, comma-separated customisations
 * on a label with no room for them, "one label per add-on" fighting "group add-ons by type".
 *
 * Every write therefore goes through `normalizeSettings`, which applies an ORDERED list of
 * named rules until the draft is self-consistent. This replaces a run of thirteen inline
 * `set`/`unset` calls in the screen component, which had three problems this shape fixes:
 *
 *  1. **The rules were invisible.** They read as imperative statements inside a callback, so
 *     nothing could test one, name one in a bug report, or tell you which one fired.
 *  2. **One predicate was computed too early.** `isBarcodeSelected` was captured BEFORE the
 *     rule that wipes the template, so the barcode rules judged a template the draft no longer
 *     had. Here every rule reads the LIVE draft, which is what the old ordering comment
 *     intended. See `barcodeFieldsRequireBarcodeInTemplate` for the one observable difference.
 *  3. **The delimiter was decided in two places** — cleared near the top, defaulted at the
 *     bottom, with an unrelated rule in between. It is now one rule that owns the field.
 *
 * ⚠️ PURE, and it must stay that way: no redux, no network, no platform API, no `structuredClone`
 * of anything but its own argument. That is what lets the whole set be reasoned about — and
 * eventually tested — without mounting a screen.
 *
 * ⚠️ ORDER IS SIGNIFICANT. Each rule may enable or defuse a later one. The array is the spec;
 * read it top to bottom.
 */
import {
  BarcodeDelimiter,
  type ItemLabelSettings,
  LabelSettingKey,
  type LabelSettingValue,
  PrintStage,
  TemplateField,
} from "../model";

/** Label sizes with room to print customisations on one comma-separated line. */
export const COMMA_SEPARATED_ALLOWED_TEMPLATE_SIZES = ["2.3*3"];

/** The template as a real array, whatever the backend sent (`""`, absent, or a list). */
export function templateFields(settings: ItemLabelSettings): string[] {
  const template = settings[LabelSettingKey.Template];
  return Array.isArray(template) ? template : [];
}

/** Does the label print a barcode at all? Gates both barcode sub-settings. */
export function printsBarcode(settings: ItemLabelSettings): boolean {
  return templateFields(settings).includes(TemplateField.Barcode);
}

/**
 * Is there room on the label for comma-separated customisations?
 *
 * Either the size is one of the large ones, or the template is sparse enough that the line is
 * free — empty (`""`, `[]`, absent) or nothing but the item name.
 *
 * Two deliberate consequences of the empty branch: a fresh unconfigured screen HAS room, and
 * because changing the size wipes the template, a size change alone never takes room away.
 * Room only disappears once the template gains a second field, or a field other than `name`.
 */
export function hasRoomForCommaSeparatedOptions(settings: ItemLabelSettings): boolean {
  const fields = templateFields(settings);
  const isSparse =
    fields.length === 0 ||
    (fields.length === 1 && fields[0] === TemplateField.Name);
  const size = settings[LabelSettingKey.TemplateSize];
  return (size ? COMMA_SEPARATED_ALLOWED_TEMPLATE_SIZES.includes(size) : false) || isSparse;
}

/**
 * Is label printing turned on for THIS sale's channel?
 *
 * The single answer to a question five call sites were each asking their own way: both apps'
 * `helper/featureRestrictionHelper#checkForPrintLabelSticker`, both apps' order-screen print
 * handlers, and web's `printItemLabelStickerOnOrderAccept`. They agreed on the rule and
 * disagreed on the guarding.
 *
 * ⚠️ **The guarding is the point.** `channelDefIds` is absent on a branch that has never
 * configured label stickers, and both apps' selectors default the whole settings object to
 * `{}` — so `settings.channelDefIds.includes(...)` throws there. Web's handler was fixed in
 * the 2026-09-11 rewrite; mobile's still carried the unguarded form until this function
 * replaced it. Routing every caller through here retires the class rather than the instance.
 *
 * It typechecked on both sides only because the apps' own `ItemLabelSettings` declared the
 * field required; core's `model` reflects the wire truth, where every field is optional.
 */
export function labelsEnabledForSale(
  settings: ItemLabelSettings | undefined,
  sale: { channelDefId?: string } | undefined
): boolean {
  const channelDefIds = settings?.[LabelSettingKey.Channels];
  return (
    !!sale?.channelDefId &&
    !!channelDefIds?.length &&
    channelDefIds.includes(sale.channelDefId)
  );
}

/**
 * ...and should they print BY THEMSELVES when an online order is accepted?
 *
 * `PrintStage.Manual` and `PrintStage.UsingKds` both answer false here: manual prints come from
 * the order screen's own action, and the KDS stage is driven by the KDS, which calls the print
 * library directly with a `printInfo` neither app supplies.
 *
 * The caller still owns the print itself — `@rista/core` cannot import either app's
 * `printHelper` (web's pulls in `html-to-image`, `jsbarcode` and the 900-line poller). This is
 * only the decision.
 */
export function shouldPrintLabelsOnAccept(
  settings: ItemLabelSettings | undefined,
  sale: { channelDefId?: string } | undefined
): boolean {
  return (
    settings?.[LabelSettingKey.PrintStage] === PrintStage.OnAccept &&
    labelsEnabledForSale(settings, sale)
  );
}

/** What changed, for the rules that only fire on a specific edit. */
export type SettingsChange = {
  key: LabelSettingKey;
  value: LabelSettingValue;
};

/**
 * One rule. Receives a MUTABLE draft (already cloned by `normalizeSettings`) plus the change
 * that triggered this pass, and edits the draft in place. Returns nothing — the draft IS the
 * result.
 */
export type NormalizationRule = {
  /** Stable id, safe to log or assert on. */
  name: string;
  /** Why this rule exists, in one sentence. */
  because: string;
  apply: (draft: ItemLabelSettings, change?: SettingsChange) => void;
};

/**
 * The rules, in application order.
 *
 * Note which ones are UNCONDITIONAL — they ignore `change` and run on every save. That is
 * deliberate: an invalid combination inherited from the backend (or written by an older client)
 * self-heals on the next save of ANY setting, rather than needing a corrective PUT on load.
 */
export const NORMALIZATION_RULES: NormalizationRule[] = [
  {
    name: "templateSizeResetsTemplate",
    because:
      "Which variables fit is a property of the size, so the old selection cannot survive a size change.",
    apply: (draft, change) => {
      if (change?.key !== LabelSettingKey.TemplateSize) return;
      draft[LabelSettingKey.Template] = [];
    },
  },
  {
    name: "barcodeFieldsRequireBarcodeInTemplate",
    because: "Barcode special fields are meaningless unless the template prints a barcode.",
    // Runs AFTER templateSizeResetsTemplate and reads the post-reset template. The old code
    // captured this predicate before the reset, so a size change left orphaned barcode fields
    // behind (hidden by the row's own isHidden, then cleared on the following save). They are
    // now cleared in the same pass.
    apply: (draft) => {
      if (printsBarcode(draft)) return;
      delete draft[LabelSettingKey.BarcodeFields];
    },
  },
  {
    name: "addOnPresentationReleasesSeparateLabels",
    because:
      "Grouping add-ons by type, or listing them inline, both replace one-label-per-add-on.",
    apply: (draft, change) => {
      const isAddOnPresentationKey =
        change?.key === LabelSettingKey.PrintByAddOnType ||
        change?.key === LabelSettingKey.CommaSeparatedOptions;
      if (!isAddOnPresentationKey || change?.value !== true) return;
      draft[LabelSettingKey.SeparateAddOnLabels] = false;
    },
  },
  {
    name: "separateLabelsSuppressAddOnPresentation",
    because:
      "The inverse of the rule above, as an invariant: one-label-per-add-on excludes both presentations.",
    // UNCONDITIONAL — heals a branch that stored a conflicting pair before these rules existed.
    apply: (draft) => {
      if (draft[LabelSettingKey.SeparateAddOnLabels] !== true) return;
      draft[LabelSettingKey.PrintByAddOnType] = false;
      draft[LabelSettingKey.CommaSeparatedOptions] = false;
    },
  },
  {
    name: "commaSeparatedOptionsNeedRoom",
    because:
      "Without room the toggle renders disabled, so the stored value must match what the user sees.",
    // UNCONDITIONAL, and reads the post-reset template — see hasRoomForCommaSeparatedOptions.
    apply: (draft) => {
      if (hasRoomForCommaSeparatedOptions(draft)) return;
      draft[LabelSettingKey.CommaSeparatedOptions] = false;
    },
  },
  {
    name: "barcodeDelimiterFollowsBarcodeFields",
    because:
      "A delimiter only means something between two barcode fields, and fields without one would concatenate.",
    // LAST, so it sees the final barcode field list. Owns the delimiter outright: the old code
    // cleared it in one place and defaulted it in another, with an unrelated rule in between.
    apply: (draft) => {
      const fields = draft[LabelSettingKey.BarcodeFields];
      const hasFields = Array.isArray(fields) && fields.length > 0;
      if (!hasFields) {
        // Explicit null, NOT a delete: the backend distinguishes "cleared" from "absent".
        draft[LabelSettingKey.BarcodeDelimiter] = null;
        return;
      }
      if (!draft[LabelSettingKey.BarcodeDelimiter]) {
        draft[LabelSettingKey.BarcodeDelimiter] = BarcodeDelimiter.Hyphen;
      }
    },
  },
];

/**
 * Apply `change` to `settings` and return a NEW, self-consistent settings object.
 *
 * Never mutates its argument. Pass no `change` to normalise in place — useful on load, or when
 * several fields moved at once and only the unconditional rules should speak.
 */
export function normalizeSettings(
  settings: ItemLabelSettings,
  change?: SettingsChange
): ItemLabelSettings {
  const draft: ItemLabelSettings = { ...settings };
  if (change) {
    (draft as Record<string, unknown>)[change.key] = change.value;
  }
  for (const rule of NORMALIZATION_RULES) {
    rule.apply(draft, change);
  }
  return draft;
}

/**
 * Which rules WOULD fire for a change, without applying them. For diagnostics and tests —
 * "why did my delimiter reset?" is answerable without stepping through a save.
 */
export function explainNormalization(
  settings: ItemLabelSettings,
  change?: SettingsChange
): string[] {
  const fired: string[] = [];
  const draft: ItemLabelSettings = { ...settings };
  if (change) {
    (draft as Record<string, unknown>)[change.key] = change.value;
  }
  for (const rule of NORMALIZATION_RULES) {
    const before = JSON.stringify(draft);
    rule.apply(draft, change);
    if (JSON.stringify(draft) !== before) fired.push(rule.name);
  }
  return fired;
}
